import { useState, useEffect, useCallback, useMemo } from 'react'
import { StatusBadge } from '../components/StatusBadge'
import { useNavigate } from 'react-router-dom'
import { PageHeader, Card, Badge } from '@pilot-hub/ui'
import { dashboardApi } from '@pilot-hub/api-client'
import {
  MapPin, Zap, Calendar, Plane, Clock, Battery,
  Crown, AlertTriangle, UserCheck, UserX,
  ChevronDown, ChevronUp, Shield, Users,
  Circle, HelpCircle, Info, XCircle, ChevronRight
} from 'lucide-react'

// ============================================================================
// Types (matching backend stats-v2 response)
// ============================================================================

interface EinsatzStat {
  abgeschlossen: number
  geplant: number
  laufend: number
  gesamt: number
}

interface StatsV2 {
  einsatz_stats: Record<string, EinsatzStat>
  flug_stats: { EINSATZ: number; UEBUNGSEINSATZ: number; UEBUNG: number; FREI: number }
  flugstunden_stats: { EINSATZ: number; UEBUNGSEINSATZ: number; UEBUNG: number; FREI: number }
  lizenzen_nach_typ: Record<string, number>
  /** Drohnen mit Handlungsbedarf (Wartungsplan, Status, HMS) */
  drohnen_wartung?: WartungsEintrag[]
  /** Akkus mit Handlungsbedarf (Bewertung wie in der Akku-Verwaltung) */
  akku_wartung?: Array<{
    id: number
    bezeichnung: string | null
    seriennummer: string | null
    modell: string | null
    zustand: 'wartung' | 'pruefen'
    gruende: string[]
  }>
  banner_daten: Array<{
    lizenz_typ: string
    abgelaufen: number
    laeuft_ab: number
    betroffene_nutzer: string[]
    farbe: 'rot' | 'gelb'
  }>
  letzte_einsaetze: Array<{
    id: number
    titel: string
    typ: string | null
    status: string | null
    beginn_geplant: string | null
    einsatzort_adresse: string | null
  }>
  geplante_einsaetze: Array<{
    id: number
    titel: string
    typ: string | null
    status: string | null
    beginn_geplant: string | null
    einsatzort_adresse: string | null
  }>
  piloten_detail: Array<{
    nutzer_id: number
    name: string
    benutzername: string
    lizenzen: Array<{ typ: string; ablaufdatum: string | null; status: string }>
    flugstunden_gesamt: number
    asb_drohnen_stunden: number
    andere_drohnen_stunden: number
    stunden_pro_drohne: Record<string, number>
    flugstunden_summe_alle: number
    isExpanded?: boolean
  }>
}

// ============================================================================
// Helpers
// ============================================================================

const einsatzTypen = ['EINSATZ', 'UEBUNGSEINSATZ', 'UEBUNG'] as const
const flugKategorien = ['EINSATZ', 'UEBUNGSEINSATZ', 'UEBUNG', 'FREI'] as const

const typIcon = (typ: string) =>
  typ === 'EINSATZ' ? <MapPin className="h-4 w-4 text-red-400" /> :
  typ === 'UEBUNGSEINSATZ' ? <Zap className="h-4 w-4 text-amber-400" /> :
  typ === 'UEBUNG' ? <Calendar className="h-4 w-4 text-blue-400" /> :
  <Battery className="h-4 w-4 text-gray-400" />

const typBadge = (typ: string | null) => {
  const colors: Record<string, string> = {
    EINSATZ: 'bg-red-600/20 text-red-300 border-red-600/30',
    UEBUNGSEINSATZ: 'bg-amber-600/20 text-amber-300 border-amber-600/30',
    UEBUNG: 'bg-blue-600/20 text-blue-300 border-blue-600/30',
  }
  return <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colors[typ || ''] || 'bg-gray-600/20 text-gray-300 border-gray-600/30'}`}>{typ ? (KAT_TEXT[typ] ?? typ) : '—'}</span>
}

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('de-DE', { dateStyle: 'short' }) : '—'

/** Anzeigenamen der Einsatz-/Flugkategorien */
const KAT_TEXT: Record<string, string> = { EINSATZ: 'Einsatz', UEBUNGSEINSATZ: 'Übungseinsatz', UEBUNG: 'Übung', FREI: 'Frei' }

const fmtStunden = (h: number) =>
  h.toFixed(1).replace('.', ',') + ' h'

const renderStatRow = (label: string, value: string, sublabel?: string) => (
  <div className="flex items-baseline justify-between gap-2 py-1.5 border-b border-dark-border/30 last:border-0">
    <span className="text-sm text-dark-text-muted truncate">{label}</span>
    <div className="flex items-baseline gap-1.5 text-right">
      <span className="text-lg font-bold text-dark-text">{value}</span>
      {sublabel && <span className="text-xs text-dark-text-muted">{sublabel}</span>}
    </div>
  </div>
)

function TypBadge({ typ }: { typ: string }) {
  const colors: Record<string, string> = {
    EINSATZ: 'bg-red-600/20 text-red-300 border-red-600/30',
    UEBUNGSEINSATZ: 'bg-amber-600/20 text-amber-300 border-amber-600/30',
    UEBUNG: 'bg-blue-600/20 text-blue-300 border-blue-600/30',
  }
  return <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colors[typ] || 'bg-gray-600/20 text-gray-300 border-gray-600/30'}`}>{KAT_TEXT[typ] ?? typ}</span>
}

// ============================================================================
// Tile Components
// ============================================================================

function TileEinsaetzeUebungen({ stats }: { stats: StatsV2 }) {
  const totalAbgeschlossen = einsatzTypen.reduce((sum, t) => sum + (stats.einsatz_stats[t]?.abgeschlossen || 0), 0)
  const totalGeplant = einsatzTypen.reduce((sum, t) => sum + (stats.einsatz_stats[t]?.geplant || 0), 0)
  const totalLaufend = einsatzTypen.reduce((sum, t) => sum + (stats.einsatz_stats[t]?.laufend || 0), 0)

  return (
    <Card className="p-4 min-w-0">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="h-5 w-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Einsätze & Übungen</h3>
        <span className="text-xs text-dark-text-muted">(Laufendes Jahr)</span>
      </div>
      <div className="space-y-2">
        {/* Headers for columns - 4 columns: Typ, Beendet, Geplant, Laufend */}
        <div className="grid grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,1fr))] sm:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))] gap-1 sm:gap-2 py-1 rounded-lg bg-dark-bg-tertiary/30 px-2 text-[9px] min-[400px]:text-[10px] xl:text-xs font-medium text-dark-text-muted uppercase tracking-normal xl:tracking-wider">
          <span>Typ</span>
          <span className="min-w-0 truncate text-center text-green-400">Beendet</span>
          <span className="min-w-0 truncate text-center text-primary-400">Geplant</span>
          <span className="min-w-0 truncate text-center text-blue-400">Laufend</span>
        </div>
        {einsatzTypen.map(typ => {
          const s = stats.einsatz_stats[typ] || { abgeschlossen: 0, geplant: 0, laufend: 0, gesamt: 0 }
          return (
            <div key={typ} className="grid grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,1fr))] sm:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))] gap-1 sm:gap-2 py-1.5 rounded-lg bg-dark-bg-tertiary/50 px-2 items-center">
              <div className="flex items-center gap-2 min-w-0">
                {typIcon(typ)}
                <span className="min-w-0 break-words text-sm font-medium text-dark-text" title={KAT_TEXT[typ] ?? typ}>{(KAT_TEXT[typ] ?? typ).replace("Übungseinsatz", "Übungs\u00ADeinsatz")}</span>
              </div>
              <div className="min-w-0 text-center">
                <span className="font-bold text-green-400 text-lg">{s.abgeschlossen}</span>
              </div>
              <div className="min-w-0 text-center">
                <span className="font-bold text-primary-400 text-lg">{s.geplant}</span>
              </div>
              <div className="min-w-0 text-center">
                <span className="font-bold text-blue-400 text-lg">{s.laufend}</span>
              </div>
            </div>
          )
        })}
        <div className="pt-2 border-t border-dark-border grid grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,1fr))] sm:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))] gap-1 sm:gap-2 px-2 items-center">
          <span className="font-medium text-dark-text">Gesamt</span>
          <div className="min-w-0 text-center">
            <span className="font-bold text-green-400 text-lg">{totalAbgeschlossen}</span>
          </div>
          <div className="min-w-0 text-center">
            <span className="font-bold text-primary-400 text-lg">{totalGeplant}</span>
          </div>
          <div className="min-w-0 text-center">
            <span className="font-bold text-blue-400 text-lg">{totalLaufend}</span>
          </div>
        </div>
      </div>
    </Card>
  )
}

function TileFluege({ stats }: { stats: StatsV2 }) {
  return (
    <Card className="p-4 min-w-0">
      <div className="flex items-center gap-2 mb-4">
        <Plane className="h-5 w-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Flüge</h3>
        <span className="text-xs text-dark-text-muted">(Laufendes Jahr)</span>
      </div>
      <div className="space-y-2">
        {flugKategorien.map(kat => {
          const count = stats.flug_stats[kat] || 0
          return (
            <div key={kat} className="flex items-center justify-between gap-3 py-1.5 rounded-lg bg-dark-bg-tertiary/50 px-3">
              <div className="flex items-center gap-2 min-w-0">
                {typIcon(kat)}
                <span className="text-sm font-medium text-dark-text truncate">{KAT_TEXT[kat] ?? kat}</span>
              </div>
              <div className="flex-1 shrink-0 text-right whitespace-nowrap">
                <span className="text-lg font-bold text-dark-text">{count}</span>
              </div>
            </div>
          )
        })}
        <div className="pt-2 border-t border-dark-border flex items-center justify-between">
          <span className="font-medium text-dark-text">Gesamt</span>
          <span className="text-lg font-bold text-primary-400">
            {(stats.flug_stats.EINSATZ||0)+(stats.flug_stats.UEBUNGSEINSATZ||0)+(stats.flug_stats.UEBUNG||0)+(stats.flug_stats.FREI||0)}
          </span>
        </div>
      </div>
    </Card>
  )
}

function TileFlugstunden({ stats }: { stats: StatsV2 }) {
  return (
    <Card className="p-4 min-w-0">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-5 w-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Flugstunden</h3>
        <span className="text-xs text-dark-text-muted">(Laufendes Jahr)</span>
      </div>
      <div className="space-y-2">
        {flugKategorien.map(kat => {
          const stunden = stats.flugstunden_stats[kat] || 0
          return (
            <div key={kat} className="flex items-center justify-between gap-3 py-1.5 rounded-lg bg-dark-bg-tertiary/50 px-3">
              <div className="flex items-center gap-2 min-w-0">
                {typIcon(kat)}
                <span className="text-sm font-medium text-dark-text truncate">{KAT_TEXT[kat] ?? kat}</span>
              </div>
              <div className="flex-1 shrink-0 text-right whitespace-nowrap">
                <span className="text-lg font-bold text-dark-text">{stunden.toFixed(1).replace('.', ',')} h</span>
              </div>
            </div>
          )
        })}
        <div className="pt-2 border-t border-dark-border flex items-center justify-between">
          <span className="font-medium text-dark-text">Gesamt</span>
          <span className="text-lg font-bold text-primary-400">
            {fmtStunden((stats.flugstunden_stats.EINSATZ||0)+(stats.flugstunden_stats.UEBUNGSEINSATZ||0)+(stats.flugstunden_stats.UEBUNG||0)+(stats.flugstunden_stats.FREI||0))}
          </span>
        </div>
      </div>
    </Card>
  )
}

function TileLizenzen({ stats }: { stats: StatsV2 }) {
  const totalLizenzen = Object.values(stats.lizenzen_nach_typ).reduce((a, b) => a + b, 0)

  return (
    <Card className="p-4 min-w-0">
      <div className="flex items-center gap-2 mb-4">
        <Crown className="h-5 w-5 text-primary-400" />
        <h3 className="font-semibold text-dark-text">Lizenzen</h3>
        <span className="text-xs text-dark-text-muted">(Übersicht)</span>
      </div>
      <div className="space-y-2 mb-4">
        {Object.entries(stats.lizenzen_nach_typ).map(([typ, count]) => (
          <div key={typ} className="flex items-center justify-between py-1.5 rounded-lg bg-dark-bg-tertiary/50 px-3">
            <span className="text-sm font-medium text-dark-text">{typ}</span>
            <span className="text-lg font-bold text-dark-text">{count}</span>
          </div>
        ))}
        <div className="pt-2 border-t border-dark-border flex items-center justify-between text-sm">
          <span className="font-medium text-dark-text">Gesamt</span>
          <span className="font-bold text-primary-400">{totalLizenzen}</span>
        </div>
      </div>
    </Card>
  )
}

// ============================================================================
// Banner: Lizenzen die ablaufen/abgelaufen pro Typ
// ============================================================================

function LizenzBanner({ stats }: { stats: StatsV2 }) {
  if (!stats.banner_daten || stats.banner_daten.length === 0) return null

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-dark-text flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-yellow-400" />
        Lizenzen – Ablauf in 6 Monaten / Abgelaufen
      </h3>
      <div className="space-y-2">
        {stats.banner_daten.map(b => (
          <div key={b.lizenz_typ} className={`rounded-xl p-4 border-l-4 ${b.farbe === 'rot' ? 'border-red-500 bg-red-500/10' : 'border-yellow-500 bg-yellow-500/10'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-dark-text">{b.lizenz_typ}</span>
                  {b.abgelaufen > 0 && <Badge variant="danger">{b.abgelaufen} abgelaufen</Badge>}
                  {b.laeuft_ab > 0 && <Badge variant="warning">{b.laeuft_ab} laufen in 6M ab</Badge>}
                </div>
                <div className="text-sm text-dark-text-muted">
                  Betroffen: {b.betroffene_nutzer.join(', ') || '—'}
                </div>
              </div>
              <div className={`flex items-center gap-2 text-lg font-bold ${b.farbe === 'rot' ? 'text-red-400' : 'text-yellow-400'}`}>
                {b.farbe === 'rot' ? <UserX className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                <span>{b.abgelaufen + b.laeuft_ab} Lizenzen</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Banner: Drohnen und Akkus mit Wartungsbedarf (je Gerät mit Grund)
// ============================================================================

type WartungsEintrag = NonNullable<StatsV2['akku_wartung']>[number]

/** Banner je Gerät mit Wartungsbedarf und Grund; Klick öffnet die Detailansicht (Akkus bzw. Drohnen). */
function WartungBanner({ liste, titel, icon, pfad, einheit }: {
  liste: WartungsEintrag[]; titel: string; icon: React.ReactNode; pfad: (id: number) => string; einheit: string
}) {
  const navigate = useNavigate()
  if (liste.length === 0) return null
  const pruefen = liste.filter(a => a.zustand === 'pruefen').length

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-dark-text flex items-center gap-2">
        {icon}
        {titel}
      </h3>
      <div className="space-y-2">
        {liste.map(a => {
          const rot = a.zustand === 'pruefen'
          return (
            <button key={a.id} type="button" onClick={() => navigate(pfad(a.id))}
              className={`w-full rounded-xl p-4 border-l-4 text-left transition-colors ${rot ? 'border-red-500 bg-red-500/10 hover:bg-red-500/15' : 'border-yellow-500 bg-yellow-500/10 hover:bg-yellow-500/15'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-semibold text-dark-text">{a.bezeichnung || `${einheit} #${a.id}`}</span>
                    <Badge variant={rot ? 'danger' : 'warning'}>{rot ? 'prüfen' : 'Wartung fällig'}</Badge>
                    <span className="text-xs text-dark-text-subtle">
                      {[a.modell, a.seriennummer ? `SN ${a.seriennummer}` : null].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  <ul className="text-sm text-dark-text-muted">
                    {a.gruende.map(g => <li key={g}>Grund: {g}</li>)}
                  </ul>
                </div>
                <div className={`flex items-center gap-1 ${rot ? 'text-red-400' : 'text-yellow-400'}`}>
                  {rot ? <XCircle className="h-5 w-5" aria-hidden /> : <AlertTriangle className="h-5 w-5" aria-hidden />}
                  <ChevronRight className="h-4 w-4 text-dark-text-subtle" aria-hidden />
                </div>
              </div>
            </button>
          )
        })}
      </div>
      {pruefen > 0 && (
        <p className="text-xs text-dark-text-subtle">{pruefen} {einheit === 'Akku' ? 'Akku(s)' : 'Drohne(n)'} sollten vor dem nächsten Einsatz geprüft werden.</p>
      )}
    </div>
  )
}

// ============================================================================
// Letzte & Geplante Einsätze
// ============================================================================

function LetzteEinsaetze({ stats }: { stats: StatsV2 }) {
  return (
    <Card>
      <h3 className="mb-4 font-semibold text-dark-text flex items-center gap-2">
        <Calendar className="h-5 w-5 text-primary-400" />
        Letzte Einsätze (beendet)
      </h3>
      {(stats.letzte_einsaetze || []).length === 0 ? (
        <p className="py-6 text-center text-sm text-dark-text-muted">Keine vergangenen Einsätze.</p>
      ) : (
        <div className="space-y-2">
          {(stats.letzte_einsaetze || []).slice(0, 5).map(e => (
            <div key={e.id} className="rounded-lg border border-dark-border bg-dark-bg-tertiary p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium text-dark-text">{e.titel}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-dark-text-muted">
                    {e.typ && <TypBadge typ={e.typ} />}
                    {e.beginn_geplant && <span>{new Date(e.beginn_geplant).toLocaleDateString('de-DE', { dateStyle: 'short' })}</span>}
                    {e.einsatzort_adresse && <span>· {e.einsatzort_adresse}</span>}
                  </div>
                </div>
                {e.status && <StatusBadge wert={e.status} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function GeplanteEinsaetze({ stats }: { stats: StatsV2 }) {
  return (
    <Card>
      <h3 className="mb-4 font-semibold text-dark-text flex items-center gap-2">
        <Calendar className="h-5 w-5 text-green-400" />
        Geplante Einsätze
      </h3>
      {(stats.geplante_einsaetze || []).length === 0 ? (
        <p className="py-6 text-center text-sm text-dark-text-muted">Keine geplanten Einsätze.</p>
      ) : (
        <div className="space-y-2">
          {(stats.geplante_einsaetze || []).slice(0, 5).map(e => (
            <div key={e.id} className="rounded-lg border border-dark-border bg-dark-bg-tertiary p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium text-dark-text">{e.titel}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-dark-text-muted">
                    {e.typ && <TypBadge typ={e.typ} />}
                    {e.beginn_geplant && <span>{new Date(e.beginn_geplant).toLocaleDateString('de-DE', { dateStyle: 'short' })}</span>}
                    {e.einsatzort_adresse && <span>· {e.einsatzort_adresse}</span>}
                  </div>
                </div>
                {e.status && <StatusBadge wert={e.status} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// Pilot Cards
// ============================================================================

type PilotLizenz = { typ: string; ablaufdatum: string | null; status: string }

/** Kurzname für die kompakte Anzeige; voller Name steht in der Warnzeile bzw. im Tooltip. */
function lizenzKurz(typ: string): string {
  const t = (typ || '').trim()
  let m: RegExpMatchArray | null
  if (/droniq/i.test(t)) return 'Droniq BOS'
  if (/A1\s*\/\s*A3/i.test(t)) return 'A1/A3'
  if ((m = t.match(/\bSTS[- ]?0?(\d)/i))) return `STS-0${m[1]}`
  if (/\bA2\b/i.test(t)) return 'A2'
  if ((m = t.match(/f(?:ü|ue|u)hrerschein(?:klasse)?\s+(\S+)/i))) return `Kl. ${m[1]}`
  if (/funk/i.test(t)) return /\bBOS\b/i.test(t) ? 'BOS-Funk' : 'Funk'
  return t.length <= 12 ? t : `${t.slice(0, 11)}…`
}

const LIZENZ_STUFE: Record<string, number> = { abgelaufen: 0, laeuft_ab: 1, gueltig: 2 }
const datumKurz = (s: string | null) => (s ? new Date(s).toLocaleDateString('de-DE') : '')
const monatJahr = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('de-DE', { month: '2-digit', year: '2-digit' }) : ''

/** Kompakte Lizenz-Chips (kritische zuerst) + je abgelaufener/bald ablaufender Lizenz eine Warnzeile. */
function PilotLizenzen({ lizenzen }: { lizenzen: PilotLizenz[] }) {
  const sortiert = [...lizenzen].sort((a, b) =>
    (LIZENZ_STUFE[a.status] ?? 3) - (LIZENZ_STUFE[b.status] ?? 3) || lizenzKurz(a.typ).localeCompare(lizenzKurz(b.typ)))
  const abgelaufen = lizenzen.filter(l => l.status === 'abgelaufen').length
  const laeuftAb = lizenzen.filter(l => l.status === 'laeuft_ab').length
  const warnungen = sortiert.filter(l => l.status === 'abgelaufen' || l.status === 'laeuft_ab')

  return (
    <div className="mb-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
        <span className="flex items-center gap-1 font-medium text-dark-text">
          <Crown className="h-3.5 w-3.5 text-primary-400" aria-hidden />Lizenzen {lizenzen.length}
        </span>
        {laeuftAb > 0 && <span className="text-yellow-400">· ⚠ {laeuftAb} läuft ab</span>}
        {abgelaufen > 0 && <span className="text-red-400">· ✕ {abgelaufen} abgelaufen</span>}
      </div>
      {lizenzen.length === 0 ? (
        <p className="text-xs text-dark-text-muted">Keine Lizenzen erfasst.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1">
            {sortiert.map((l, i) => {
              const rot = l.status === 'abgelaufen', gelb = l.status === 'laeuft_ab'
              return (
                <span key={i}
                  title={`${l.typ}${l.ablaufdatum ? ` – ${rot ? 'abgelaufen' : 'gültig bis'} ${datumKurz(l.ablaufdatum)}` : ' – unbefristet'}`}
                  className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${rot
                    ? 'border-red-500/60 bg-red-500/20 text-red-300'
                    : gelb ? 'border-yellow-500/60 bg-yellow-500/15 text-yellow-300'
                    : 'border-emerald-500/25 bg-emerald-500/5 text-emerald-300/90'}`}>
                  <span aria-hidden>{rot ? '✕' : gelb ? '⚠' : '✓'}</span>
                  {lizenzKurz(l.typ)}
                  {gelb && l.ablaufdatum && <span className="font-normal opacity-80">{monatJahr(l.ablaufdatum)}</span>}
                </span>
              )
            })}
          </div>
          {warnungen.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-[11px] leading-snug">
              {warnungen.map((l, i) => (
                <li key={i} className={l.status === 'abgelaufen' ? 'text-red-300' : 'text-yellow-300'}>
                  {l.status === 'abgelaufen' ? '✕' : '⚠'} {l.typ} – {l.status === 'abgelaufen' ? 'abgelaufen' : 'bis'} {datumKurz(l.ablaufdatum)}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

function PilotCard({ pilot, onToggle }: { pilot: StatsV2['piloten_detail'][0] & { isExpanded?: boolean }; onToggle: () => void }) {
  return (
    <Card className="p-4 min-w-0">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 font-medium text-white">
              {pilot.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="truncate font-semibold text-dark-text">{pilot.name}</div>
              <div className="text-xs text-dark-text-muted">@{pilot.benutzername}</div>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 p-1 rounded-lg hover:bg-dark-bg-tertiary transition-colors"
          aria-expanded={!!pilot.isExpanded}
          aria-label={pilot.isExpanded ? 'Flugstunden pro Drohne einklappen' : 'Flugstunden pro Drohne ausklappen'}
          title={pilot.isExpanded ? 'Einklappen' : 'Flugstunden pro Drohne anzeigen'}
        >
          <ChevronDown className={`h-5 w-5 text-dark-text-muted transition-transform ${pilot.isExpanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Zusammenfassung (immer sichtbar) */}
      <div className="grid grid-cols-3 gap-3 mb-3 p-3 [&>*]:min-w-0 rounded-lg bg-dark-bg-tertiary/50">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary-400">{pilot.flugstunden_summe_alle.toFixed(1).replace('.', ',')}</div>
          <div className="text-xs text-dark-text-muted">Flugstunden ∑</div>
        </div>
        <div className="text-center border-x border-dark-border/30">
          <div className="text-2xl font-bold text-green-400">{pilot.asb_drohnen_stunden.toFixed(1).replace('.', ',')}</div>
          <div className="text-xs text-dark-text-muted">ASB-Drohnen</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-400">{pilot.andere_drohnen_stunden.toFixed(1).replace('.', ',')}</div>
          <div className="text-xs text-dark-text-muted">Andere Drohnen</div>
        </div>
      </div>

      <PilotLizenzen lizenzen={pilot.lizenzen || []} />

      {/* Detail-Bereich (einklappbar über den Pfeil) */}
      {pilot.isExpanded && (
      <div>
        {/* Flugstunden pro Drohne */}
        <div className="mb-4 p-3 rounded-lg bg-dark-bg-tertiary/50 border border-dark-border/30">
          <h4 className="font-medium text-dark-text mb-3 flex items-center gap-2">
            <Plane className="h-4 w-4 text-primary-400" />
            Flugstunden pro Drohne (laufendes Jahr)
          </h4>
          <div className="space-y-2">
            {Object.entries(pilot.stunden_pro_drohne || {}).length > 0 ? (
              <div>
                {Object.entries(pilot.stunden_pro_drohne).map(([modell, stunden]) => (
                  <div key={modell} className="flex items-center justify-between py-1.5 rounded bg-dark-bg border border-dark-border/30 px-2">
                    <span className="text-sm text-dark-text">{modell}</span>
                    <span className="font-bold text-primary-400">{stunden.toFixed(1).replace('.', ',')} h</span>
                  </div>
                ))}
                <div className="pt-2 border-t border-dark-border/30 flex items-center justify-between">
                  <span className="text-sm font-medium text-dark-text">Summe ASB-Drohnen</span>
                  <span className="font-bold text-green-400">{pilot.asb_drohnen_stunden.toFixed(1).replace('.', ',')} h</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-dark-text-muted">Keine Flüge mit ASB-Drohnen erfasst.</p>
            )}
            <div className="flex items-center justify-between text-sm text-dark-text-muted mt-2">
              <span>Andere Drohnen (Summe)</span>
              <span className="font-medium text-blue-400">{pilot.andere_drohnen_stunden.toFixed(1).replace('.', ',')} h</span>
            </div>
          </div>
        </div>
      </div>
      )}
    </Card>
  )
}


// ============================================================================
// Handlungsbedarf: eine Zeile mit Zählern und Links (bei 0 grün „Nichts offen“)
// ============================================================================

function Handlungsbedarf({ stats }: { stats: StatsV2 }) {
  const navigate = useNavigate()
  const akkuWartung = stats.akku_wartung ?? []
  const wartung = (stats.drohnen_wartung ?? []).length + akkuWartung.filter(a => a.zustand !== 'pruefen').length
  const akkusPruefen = akkuWartung.filter(a => a.zustand === 'pruefen').length
  const laufend = einsatzTypen.reduce((sum, t) => sum + (stats.einsatz_stats[t]?.laufend || 0), 0)
  const punkte = [
    { n: wartung, text: 'Wartung', pfad: '/drones', ton: 'border-amber-500/40 bg-amber-500/10 text-amber-100' },
    { n: akkusPruefen, text: akkusPruefen === 1 ? 'Akku prüfen' : 'Akkus prüfen', pfad: '/akkus', ton: 'border-amber-500/40 bg-amber-500/10 text-amber-100' },
    { n: laufend, text: laufend === 1 ? 'laufender Einsatz' : 'laufende Einsätze', pfad: '/missions', ton: 'border-sky-500/40 bg-sky-500/10 text-sky-100' },
  ].filter(p => p.n > 0)
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="handlungsbedarf" role="group" aria-label="Handlungsbedarf">
      <span className="text-sm font-semibold text-dark-text">Handlungsbedarf</span>
      {punkte.length === 0
        ? <span className="inline-flex min-h-[36px] items-center rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3.5 text-sm text-emerald-200">Nichts offen</span>
        : punkte.map(p => (
          <button key={p.text} type="button" onClick={() => navigate(p.pfad)}
            className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3.5 text-sm hover:brightness-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${p.ton}`}>
            <span className="font-semibold tabular-nums">{p.n}</span><span>{p.text}</span>
          </button>
        ))}
    </div>
  )
}

// ============================================================================
// Main Dashboard
// ============================================================================

export function DashboardPage() {
  const [stats, setStats] = useState<StatsV2 | null>(null)
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [expandedPiloten, setExpandedPiloten] = useState<Set<number>>(new Set())

  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const r = await dashboardApi.getStatsV2()
      const data = r.data as unknown as StatsV2
      // Add isExpanded flag to pilots
      if (data.piloten_detail) {
        data.piloten_detail = data.piloten_detail.map(p => ({
          ...p,
          isExpanded: false // default collapsed
        }))
      }
      setStats(data)
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Dashboard-Daten konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { laden() }, [laden])

  const togglePilot = useCallback((id: number) => {
    setExpandedPiloten(prev => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }, [])

  if (loading) return <div className="py-12 text-center text-dark-text-muted">Lade Dashboard…</div>
  if (fehler) return (
    <div className="space-y-6">
      <PageHeader title="" subtitle="Admin-Übersicht" />
      <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">{fehler}</div>
    </div>
  )
  if (!stats) return null

  return (
    <div className="space-y-6">
      <PageHeader
        title=""
        subtitle="Admin-Übersicht ASB Drohnenstaffel – Laufendes Jahr"
      />

      <Handlungsbedarf stats={stats} />

      {/* === 1. OBERE KACHEL-REIHE (4 Tiles) === */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-4 [&>*]:min-w-0">
        <TileEinsaetzeUebungen stats={stats} />
        <TileFluege stats={stats} />
        <TileFlugstunden stats={stats} />
        <TileLizenzen stats={stats} />
      </div>

      {/* === 2. BANNER: Lizenzen Ablauf/Abgelaufen pro Typ === */}
      <LizenzBanner stats={stats} />

      {/* === 2b. BANNER: Drohnen und Akkus mit Wartungsbedarf === */}
      <WartungBanner liste={stats.drohnen_wartung ?? []} titel="Drohnen – Wartung fällig / prüfen" einheit="Drohne"
        icon={<Plane className="h-5 w-5 text-yellow-400" />} pfad={id => `/drones?drohne=${id}`} />
      <WartungBanner liste={stats.akku_wartung ?? []} titel="Akkus – Wartung fällig / prüfen" einheit="Akku"
        icon={<Battery className="h-5 w-5 text-yellow-400" />} pfad={id => `/akkus?akku=${id}`} />

      {/* === 3. LETZTE & GEPLANTE EINSÄTZE (2 Karten) === */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <LetzteEinsaetze stats={stats} />
        <GeplanteEinsaetze stats={stats} />
      </div>

      {/* === 4. PILOTEN-KARTEN === */}
      <div className="space-y-6">
        <h2 className="font-semibold text-dark-text flex items-center gap-2">
          <Users className="h-5 w-5 text-primary-400" />
          Piloten – Lizenzen & Flugstunden (laufendes Jahr)
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 [&>*]:min-w-0">
          {(stats.piloten_detail || []).map(pilot => (
            <PilotCard
              key={pilot.nutzer_id}
              pilot={{ ...pilot, isExpanded: expandedPiloten.has(pilot.nutzer_id) }}
              onToggle={() => togglePilot(pilot.nutzer_id)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default DashboardPage